// Shared types + helpers for the Community feed filters.
// Kept framework-agnostic so both the data layer (hazardService) and the UI
// (CommunityFilterPanel / MainLayout / Community) can import without cycles.

/** Hazard severity derived from community risk votes (reaction_type_name). */
export type SeverityLevel = 'low' | 'medium' | 'high' | 'extreme';

/** Resolution-status filter. */
export type ResolvedFilter = 'all' | 'resolved' | 'unresolved';

/** Which single field the free-text search matches against. */
export type SearchField = 'description' | 'tags';

/** Ascending severity order. Later = more severe (used for tie-breaking). */
export const SEVERITY_ORDER: SeverityLevel[] = ['low', 'medium', 'high', 'extreme'];

export interface CommunityFilters {
  /** Show only the current user's hazards. */
  myHazardsOnly: boolean;
  /** Inclusive lower bound (local YYYY-MM-DD). null = no lower bound (all time). */
  dateStart: string | null;
  /** Inclusive upper bound (local YYYY-MM-DD). null = no upper bound. */
  dateEnd: string | null;
  /** Author user_ids to include (OR within the set). Empty = all authors. */
  userIds: string[];
  /** Dominant-vote severities to include (OR within the set). Empty = all. */
  severities: SeverityLevel[];
  /** Resolution status. */
  status: ResolvedFilter;
  /** Regions (provinces) to include (OR within the set). Empty = all. */
  regions: string[];
  /** Which field the search term applies to. */
  searchField: SearchField;
  /** Free-text search term. */
  searchTerm: string;
}

export const defaultCommunityFilters: CommunityFilters = {
  myHazardsOnly: false,
  dateStart: null,
  dateEnd: null,
  userIds: [],
  severities: [],
  status: 'all',
  regions: [],
  searchField: 'description',
  searchTerm: '',
};

/** True when tag/description search would narrow the result set. */
export function isSearchFilterActive(f: CommunityFilters): boolean {
  return f.searchTerm.trim().length > 0;
}

/** True when at least one filter would narrow the result set. */
export function isAnyFilterActive(f: CommunityFilters): boolean {
  return (
    f.myHazardsOnly ||
    f.dateStart !== null ||
    f.dateEnd !== null ||
    f.userIds.length > 0 ||
    f.severities.length > 0 ||
    f.status !== 'all' ||
    f.regions.length > 0 ||
    isSearchFilterActive(f)
  );
}

/** Client-side tag/description match used for Dexie fallback and live prepend. */
export function hazardMatchesSearch(
  hazard: { description?: string; tags?: string[] },
  f: CommunityFilters
): boolean {
  const term = f.searchTerm.trim().toLowerCase();
  if (!term) return true;
  if (f.searchField === 'tags') {
    return (hazard.tags || []).some((t) => t.toLowerCase().includes(term));
  }
  return (hazard.description || '').toLowerCase().includes(term);
}

/** Number of distinct active filter groups (for the funnel badge). */
export function countActiveFilters(f: CommunityFilters): number {
  let n = 0;
  if (f.myHazardsOnly) n++;
  if (f.dateStart !== null || f.dateEnd !== null) n++;
  if (f.userIds.length > 0) n++;
  if (f.severities.length > 0) n++;
  if (f.status !== 'all') n++;
  if (f.regions.length > 0) n++;
  if (f.searchTerm.trim().length > 0) n++;
  return n;
}

/** Available facet values for populating the user/region pickers. */
export interface CommunityFilterFacets {
  users: { userId: string; username: string }[];
  regions: string[];
}
